/* =========================================================================
   OhMy! runtime — the presentation layer of the live theme's theme.js,
   imported for the header bar, the buy box (Kaufbox) and the PDP
   stack / UGC sections.

   WHAT IS DELIBERATELY NOT HERE
   -----------------------------
   The absorb theme owns the cart. Its theme.js already binds
   `submit` on `form[data-ajax-cart]` and drives its own drawer via
   ThemeCart / ThemeOverlay. The live theme bound the SAME selector, so
   shipping both verbatim would add every item to the cart twice.

   So this file drops the live theme's:
     - submit handler on form[data-ajax-cart]   -> absorb's Cart.add
     - refreshCartDrawer / openDrawer / closeDrawer -> absorb's Overlay
     - cart line +/- / remove / qty handlers     -> absorb's Cart.change
     - the initial /cart.js count fetch          -> absorb does it

   Everything that remains touches markup absorb does not use, so the two
   runtimes never bind the same node. Checked: absorb binds none of
   [data-line-*], [data-qty-*], [data-sort-select], [data-checkout-now],
   and uses `.qty` where this file uses `.om-qty`.
   ========================================================================= */
(function () {
  'use strict';

  var settings = window.OhMy || {};
  var strings = settings.strings || {};

  function qs(sel, ctx) { return (ctx || document).querySelector(sel); }
  function qsa(sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); }

  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    var value = '';
    var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    var formatString = format || settings.moneyFormat || '€{{amount_with_comma_separator}}';

    function formatWithDelimiters(number, precision, thousands, decimal) {
      precision = typeof precision === 'undefined' ? 2 : precision;
      thousands = thousands || ',';
      decimal = decimal || '.';
      if (isNaN(number) || number == null) return 0;
      number = (number / 100.0).toFixed(precision);
      var parts = number.split('.');
      var dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      var centsAmount = parts[1] ? decimal + parts[1] : '';
      return dollarsAmount + centsAmount;
    }

    var match = formatString.match(placeholderRegex);
    switch (match && match[1]) {
      case 'amount': value = formatWithDelimiters(cents, 2); break;
      case 'amount_no_decimals': value = formatWithDelimiters(cents, 0); break;
      case 'amount_with_comma_separator': value = formatWithDelimiters(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = formatWithDelimiters(cents, 0, '.', ','); break;
      default: value = formatWithDelimiters(cents, 2);
    }
    return formatString.replace(placeholderRegex, value);
  }

  var toastTimer;
  function toast(message) {
    var el = qs('#OmToast');
    if (!el || !message) return;
    el.textContent = message;
    el.hidden = false;
    clearTimeout(toastTimer);
    toastTimer = setTimeout(function () { el.hidden = true; }, 2600);
  }

  function parseItems(str) {
    var items = [];
    (str || '').split(',').forEach(function (pair) {
      var parts = pair.split(':');
      var id = parseInt(parts[0], 10);
      if (id) items.push({ id: id, quantity: parseInt(parts[1], 10) || 1 });
    });
    return items;
  }

  /* Hand the cart back to the absorb runtime rather than running a second one. */
  function afterCartChange() {
    if (window.ThemeCart && typeof window.ThemeCart.refresh === 'function') {
      Promise.resolve(window.ThemeCart.refresh()).then(function () {
        if (window.ThemeOverlay && typeof window.ThemeOverlay.open === 'function') {
          window.ThemeOverlay.open('CartDrawer');
        }
      });
    } else {
      window.location.href = '/cart';
    }
  }

  /* ---------- add several variants at once (the Stack section) ---------- */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-add-items]');
    if (!btn) return;
    e.preventDefault();
    var items = parseItems(btn.getAttribute('data-add-items'));
    if (!items.length) return;
    btn.setAttribute('aria-disabled', 'true');
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ items: items })
    })
      .then(function (r) { return r.json().then(function (j) { return { ok: r.ok, json: j }; }); })
      .then(function (res) {
        if (!res.ok) { toast(res.json.description || strings.error); return; }
        var code = btn.getAttribute('data-discount');
        if (code) {
          fetch('/discount/' + encodeURIComponent(code), { method: 'GET' }).finally(afterCartChange);
        } else {
          afterCartChange();
        }
        toast(strings.added);
      })
      .catch(function () { toast(strings.error); })
      .finally(function () { btn.removeAttribute('aria-disabled'); });
  });

  /* ---------- quantity stepper inside the om buy box (absorb uses .qty) ---------- */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-qty-minus], [data-qty-plus]');
    if (!btn) return;
    var wrap = btn.closest('.om-qty');
    if (!wrap) return;
    var input = qs('input', wrap);
    if (!input) return;
    var v = parseInt(input.value, 10) || 1;
    input.value = btn.hasAttribute('data-qty-minus') ? Math.max(1, v - 1) : v + 1;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /* ---------- add then go straight to checkout ---------- */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-checkout-now]');
    if (!btn) return;
    e.preventDefault();
    var form = btn.closest('form[data-ajax-cart]') || qs('form[data-ajax-cart]');
    if (!form) return;
    btn.setAttribute('aria-disabled', 'true');
    fetch('/cart/add.js', { method: 'POST', body: new FormData(form), headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(function (r) { return r.json().then(function (j) { return { ok: r.ok, json: j }; }); })
      .then(function (res) {
        if (!res.ok) { toast(res.json.description || strings.error); btn.removeAttribute('aria-disabled'); return; }
        window.location.href = '/checkout';
      })
      .catch(function () { toast(strings.error); btn.removeAttribute('aria-disabled'); });
  });

  /* ---------- product gallery (scroll-snap slider + thumbs + dots) ---------- */
  function galleryParts() {
    var track = qs('[data-gallery-track]');
    if (!track) return null;
    return { track: track, slides: qsa('.om-pdp__slide', track), dots: qsa('[data-gallery-dots] span'), thumbs: qsa('.om-pdp__thumb') };
  }
  function markGallery(index) {
    var g = galleryParts(); if (!g) return;
    g.dots.forEach(function (d, i) { d.classList.toggle('is-active', i === index); });
    qsa('.om-pdp__thumb').forEach(function (t) { t.setAttribute('aria-current', 'false'); });
    if (g.thumbs[index]) g.thumbs[index].setAttribute('aria-current', 'true');
  }
  function goToIndex(index, smooth) {
    var g = galleryParts(); if (!g || !g.slides[index]) return;
    g.track.scrollTo({ left: g.slides[index].offsetLeft, behavior: smooth === false ? 'auto' : 'smooth' });
    markGallery(index);
  }
  function goToMedia(mediaId) {
    var g = galleryParts(); if (!g) return;
    var idx = g.slides.findIndex(function (sl) { return sl.getAttribute('data-media-id') === String(mediaId); });
    if (idx >= 0) goToIndex(idx);
  }
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('.om-pdp__thumb');
    if (!btn) return;
    goToIndex(parseInt(btn.getAttribute('data-index'), 10) || 0);
  });

  function initGallery() {
    var g = galleryParts();
    if (!g || g.track.dataset.omReady === 'true') return;
    g.track.dataset.omReady = 'true';
    var t;
    g.track.addEventListener('scroll', function () {
      clearTimeout(t);
      t = setTimeout(function () {
        var w = g.track.clientWidth || 1;
        markGallery(Math.round(g.track.scrollLeft / w));
      }, 80);
    }, { passive: true });
  }

  /* ---------- generic horizontal scrollers: dots ---------- */
  function initScrollers() {
    qsa('[data-scroller]').forEach(function (sc) {
      if (sc.dataset.omReady === 'true') return;
      sc.dataset.omReady = 'true';
      var dotsWrap = sc.parentElement && sc.parentElement.querySelector('[data-scroller-dots]');
      if (!dotsWrap) return;
      var items = Array.prototype.slice.call(sc.children);
      if (items.length < 2) { dotsWrap.hidden = true; return; }
      dotsWrap.innerHTML = items.map(function (_, i) { return '<span' + (i === 0 ? ' class="is-active"' : '') + '></span>'; }).join('');
      var dots = qsa('span', dotsWrap), t;
      sc.addEventListener('scroll', function () {
        clearTimeout(t);
        t = setTimeout(function () {
          var idx = 0, min = Infinity;
          items.forEach(function (it, i) { var d = Math.abs(it.offsetLeft - sc.scrollLeft); if (d < min) { min = d; idx = i; } });
          dots.forEach(function (d, i) { d.classList.toggle('is-active', i === idx); });
        }, 80);
      }, { passive: true });
    });
  }

  /* ---------- mobile menu (header bar) ---------- */
  function closeMobileMenu() {
    var m = qs('#MobileMenu');
    if (m) { m.classList.remove('is-open'); document.body.style.overflow = ''; }
  }
  document.addEventListener('click', function (e) {
    if (e.target.closest('[data-menu-open]')) {
      var m = qs('#MobileMenu');
      if (m) { m.classList.add('is-open'); document.body.style.overflow = 'hidden'; }
    }
    if (e.target.closest('[data-menu-close]') || e.target.classList.contains('om-mobile-menu__overlay')) {
      closeMobileMenu();
    }
  });
  document.addEventListener('keydown', function (e) { if (e.key === 'Escape') closeMobileMenu(); });

  /* ---------- buy box: variants, supply packs, sampler, sticky bar ---------- */
  function initBuyBox() {
    qsa('[data-product-form]').forEach(function (container) {
      if (container.dataset.omReady === 'true') return;
      var jsonEl = qs('[data-product-json]', container);
      if (!jsonEl) return;
      var product;
      try { product = JSON.parse(jsonEl.textContent); } catch (err) { return; }
      container.dataset.omReady = 'true';

      var form = qs('form[data-ajax-cart]', container);
      var idInput = qs('input[name="id"]', container);
      var qtyInput = qs('input[name="quantity"]', container);
      var buyBtn = qs('[data-add-button]', container);
      var buyBtnText = qs('[data-add-button-text]', container);
      var totalEl = qs('[data-total]', container);
      var compareEl = qs('[data-total-compare]', container);
      var stickyPrice = qs('[data-sticky-price]');
      var stickyMeta = qs('[data-sticky-meta]');
      var stickyCount = qs('[data-sticky-count]');
      var samplerRow = qs('[data-pack-row="sampler"]', container);
      var samplerNote = qs('[data-sampler-note]', container);
      var packs = settings.packs || { enabled: false, pct2: 0, pct3: 0 };
      var packDays = parseInt(container.getAttribute('data-pack-days'), 10) || 30;
      var supplyStr = strings.supply || '{{ days }} Tage Vorrat';

      function selectedOptions() {
        return qsa('fieldset[data-option]', container).map(function (fs) {
          var checked = qs('input:checked', fs);
          return checked ? checked.value : null;
        });
      }
      function findVariant(opts) {
        if (!qsa('fieldset[data-option]', container).length) return product.variants[0];
        return product.variants.find(function (v) {
          return v.options.every(function (val, i) { return val === opts[i]; });
        });
      }
      function packValue() {
        var p = qs('input[name="pack"]:checked', container);
        return p ? p.value : '1';
      }
      function pctFor(n) {
        if (!packs.enabled) return 0;
        if (n >= 3) return packs.pct3 || 0;
        if (n === 2) return packs.pct2 || 0;
        return 0;
      }
      function samplerItems() {
        return samplerRow ? parseItems(samplerRow.getAttribute('data-sampler-items')) : [];
      }
      function samplerFull() {
        return samplerItems().reduce(function (sum, it) {
          var v = product.variants.find(function (x) { return x.id === it.id; });
          return sum + (v ? v.price * it.quantity : 0);
        }, 0);
      }
      function supplyLabel(n) { return supplyStr.replace('{{ days }}', n * packDays); }

      function updateMedia(variant) {
        if (!variant || !variant.featured_image) return;
        goToMedia(variant.featured_image.id);
        var st = qs('[data-sticky-img]');
        if (st) st.src = variant.featured_image.src;
      }

      function render() {
        var variant = findVariant(selectedOptions());
        if (!variant) {
          if (buyBtn) buyBtn.setAttribute('disabled', 'disabled');
          if (buyBtnText && buyBtn) buyBtnText.textContent = buyBtn.getAttribute('data-text-unavailable');
          return;
        }
        if (idInput) idInput.value = variant.id;
        var pv = packValue();
        var isSampler = pv === 'sampler' && !!samplerRow;
        var n = isSampler ? (parseInt(samplerRow.getAttribute('data-sampler-count'), 10) || samplerItems().length) : (parseInt(pv, 10) || 1);
        if (qtyInput) qtyInput.value = isSampler ? 1 : n;
        if (form) {
          if (isSampler) form.setAttribute('data-sampler-items', samplerRow.getAttribute('data-sampler-items'));
          else form.removeAttribute('data-sampler-items');
        }
        container.classList.toggle('is-sampler', isSampler);
        if (samplerNote) samplerNote.hidden = !isSampler;

        qsa('[data-pack-row]', container).forEach(function (row) {
          var key = row.getAttribute('data-pack-row');
          var count, full;
          if (key === 'sampler') {
            count = parseInt(row.getAttribute('data-sampler-count'), 10) || samplerItems().length;
            full = samplerFull();
          } else {
            count = parseInt(key, 10);
            full = variant.price * count;
          }
          var pct = pctFor(count);
          var total = Math.round(full * (100 - pct) / 100);
          var per = count ? Math.round(total / count) : total;
          var priceEl = qs('[data-pack-price]', row);
          var perEl = qs('[data-pack-per]', row);
          var compEl = qs('[data-pack-compare]', row);
          var saveEl = qs('[data-pack-save]', row);
          if (priceEl) priceEl.textContent = formatMoney(total);
          if (perEl) perEl.textContent = formatMoney(per) + ' ' + (strings.perPack || '');
          if (compEl) { compEl.textContent = pct ? formatMoney(full) : ''; compEl.hidden = !pct; }
          if (saveEl) { saveEl.textContent = pct ? String(strings.save || '').replace('{{ pct }}', pct) : ''; saveEl.hidden = !pct; }
        });

        var pct = pctFor(n);
        var full = isSampler ? samplerFull() : variant.price * n;
        var total = Math.round(full * (100 - pct) / 100);
        if (totalEl) totalEl.textContent = formatMoney(total);
        if (compareEl) { compareEl.textContent = pct ? formatMoney(full) : ''; compareEl.hidden = !pct; }
        if (stickyPrice) stickyPrice.textContent = formatMoney(total);
        if (stickyMeta) {
          var vt = (variant.title && variant.title !== 'Default Title') ? variant.title + ' · ' : '';
          var nameEl = samplerRow && qs('.om-pack__name', samplerRow);
          var samplerName = nameEl && nameEl.childNodes[0] ? (nameEl.childNodes[0].textContent || '').trim() : '';
          stickyMeta.textContent = isSampler ? (samplerName + ' · ' + supplyLabel(n)) : (vt + supplyLabel(n));
        }
        if (stickyCount) stickyCount.textContent = n;

        if (buyBtn) {
          var available = isSampler
            ? samplerItems().every(function (it) { var v = product.variants.find(function (x) { return x.id === it.id; }); return v && v.available; })
            : variant.available;
          if (available) {
            buyBtn.removeAttribute('disabled');
            if (buyBtnText) buyBtnText.textContent = buyBtn.getAttribute('data-text-add');
          } else {
            buyBtn.setAttribute('disabled', 'disabled');
            if (buyBtnText) buyBtnText.textContent = buyBtn.getAttribute('data-text-soldout');
          }
        }
        if (history.replaceState && qsa('fieldset[data-option]', container).length) {
          var url = new URL(window.location.href);
          url.searchParams.set('variant', variant.id);
          history.replaceState({}, '', url.toString());
        }
      }

      qsa('fieldset[data-option] input[type="radio"]', container).forEach(function (radio) {
        radio.addEventListener('change', function () {
          var fs = radio.closest('fieldset');
          var lbl = fs && qs('[data-option-value]', fs);
          if (lbl) lbl.textContent = radio.value;
          render();
          updateMedia(findVariant(selectedOptions()));
        });
      });
      qsa('input[name="pack"]', container).forEach(function (r) { r.addEventListener('change', render); });
      render();

      var sticky = qs('#OmSticky');
      if (sticky && buyBtn && 'IntersectionObserver' in window) {
        var io = new IntersectionObserver(function (entries) {
          entries.forEach(function (en) {
            var below = en.boundingClientRect.top < 0;
            sticky.classList.toggle('is-visible', !en.isIntersecting && below);
          });
        }, { threshold: 0 });
        io.observe(buyBtn);
      }
    });
  }

  /* The sticky bar's button just re-submits the real form, so absorb's
     Cart.add picks it up like any other add. */
  document.addEventListener('click', function (e) {
    var b = e.target.closest('[data-sticky-add]');
    if (!b) return;
    e.preventDefault();
    var form = qs('form[data-ajax-cart]');
    if (!form) return;
    if (form.requestSubmit) form.requestSubmit();
    else form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
  });

  function boot() { initBuyBox(); initGallery(); initScrollers(); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', boot);
})();
